import { Modal, Box, IconButton, TextField, Button } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import apiCumbres from "../axios/axios";
import { useEffect, useState } from "react";
import LoadingSpinner from '../Spinner';
import { Flip, ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { toast_config } from "./toastify/config";
import { styleBtn, textFieldStyle } from "./Styles";

const ModalEditCliente = ({ open, onClose, ic }) => {

    const [infoCliente, setInfoCliente] = useState({
        nombre: '',
        paterno: '',
        materno: '',
        direccion: '',
        telefono: '',
        notas: '',
        correo: ''
    });

    const [infoEdit, setInfoEdit] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const informacionCliente = async () => {
        if (!ic) return;
        try {
            setIsLoading(true);
            const res = await apiCumbres.get(`/clientes/informacion-cliente/${ic}`);
            setInfoCliente(res.data);
        } catch (e) {
            toast.error("Error al obtener información", toast_config);
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (open) informacionCliente();
        if (!open) {
            setInfoEdit({});
            setInfoCliente({
                nombre: '',
                paterno: '',
                materno: '',
                direccion: '',
                telefono: '',
                notas: '',
                correo: ''
            });
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInfoCliente(prev => ({ ...prev, [name]: value.toUpperCase() }));
        setInfoEdit(prev => ({ ...prev, [name]: value.toUpperCase() }));
    };

    const editarCliente = async () => {
        if (!Object.keys(infoEdit).length) {
            return toast.error("Debes editar al menos un campo", toast_config);
        }
        try {
            await apiCumbres.put(`/clientes/editar/${ic}`, infoEdit);
            toast.success("Cliente actualizado", toast_config);
            onClose();
        } catch {
            toast.error("Error al editar cliente", toast_config);
        }
    };

    const MotionFieldX = ({ children, delay = 0, angle = 20 }) => (
        <motion.div
            initial={{ opacity: 0, x: angle }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay }}
        >
            {children}
        </motion.div>
    );

    const MotionFieldY = ({ children, delay = 0, angle = 20 }) => (
        <motion.div
            initial={{ opacity: 0, y: angle }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay }}
        >
            {children}
        </motion.div>
    );

    return (
        <>
            <AnimatePresence>
                {open && (
                    <Modal open={open} onClose={onClose}
                        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
                    >
                        <>
                            {isLoading && <LoadingSpinner />}

                            {/* Backdrop */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                style={{
                                    position: "fixed",
                                    inset: 0,
                                    background: "rgba(0,0,0,0.45)"
                                }}
                                onClick={onClose}
                            />

                            {/* Modal */}
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Box
                                    sx={{
                                        width: "min(1200px, 95vw)",
                                        maxHeight: "90vh",     // 🔑 permite crecer o encogerse
                                        minHeight: "300px",
                                        bgcolor: "background.paper",
                                        borderRadius: "12px",
                                        boxShadow: 24,
                                        position: "relative",
                                        mx: "auto",
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Cerrar */}
                                    <IconButton
                                        onClick={onClose}
                                        sx={{ position: "absolute", top: 8, right: 8 }}
                                    >
                                        <HighlightOffIcon color="error" />
                                    </IconButton>

                                    {/* HEADER */}
                                    <Box sx={{ p: 3, flexShrink: 0 }}>
                                        <motion.h1
                                            initial={{ opacity: 0, y: -90 }}
                                            animate={{ y: 0, opacity: 1 }}
                                            transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                                        >
                                            Información del Cliente
                                        </motion.h1>
                                        <hr />
                                    </Box>

                                    {/* FORM SCROLL */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            overflowY: "auto",   // 🔥 aquí vive el scroll vertical
                                            overflowX: "hidden",
                                            minWidth: 0,
                                            px: 3,
                                            pb: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                    xs: "1fr",          // móvil
                                                    sm: "repeat(2, 1fr)", // tablet
                                                    lg: "repeat(3, 1fr)", // desktop 
                                                },
                                                gap: 2,
                                                marginTop: 2
                                            }}
                                        >
                                            <MotionFieldX delay={0.2} angle={-50}>
                                                <TextField label="Nombre" name="nombre" size="small" fullWidth sx={textFieldStyle} value={infoCliente.nombre} onChange={handleChange} />
                                            </MotionFieldX>

                                            <MotionFieldY delay={0.3} angle={-50}>
                                                <TextField label="Paterno" name="paterno" size="small" fullWidth sx={textFieldStyle} value={infoCliente.paterno} onChange={handleChange} />
                                            </MotionFieldY>

                                            <MotionFieldX delay={0.4} angle={50}>
                                                <TextField label="Materno" name="materno" size="small" fullWidth sx={textFieldStyle} value={infoCliente.materno} onChange={handleChange} />
                                            </MotionFieldX>


                                            <Box sx={{ gridColumn: { xs: "1", md: "span 3" } }}>
                                                <MotionFieldX delay={0.5} angle={-50}>
                                                    <TextField label="Dirección" name="direccion" size="small" fullWidth sx={textFieldStyle} value={infoCliente.direccion} onChange={handleChange} />
                                                </MotionFieldX>
                                            </Box>


                                            <MotionFieldX delay={0.6} angle={-50}>
                                                <TextField label="Teléfono" name="telefono" size="small" fullWidth sx={textFieldStyle} value={infoCliente.telefono} onChange={handleChange} />
                                            </MotionFieldX>

                                            <MotionFieldX delay={0.7} angle={50}>
                                                <TextField label="Correo" name="correo" size="small" fullWidth sx={textFieldStyle} value={infoCliente.correo} onChange={handleChange} />
                                            </MotionFieldX>


                                            <Box sx={{ gridColumn: { xs: "1", md: "span 3" } }}>
                                                <MotionFieldX delay={0.8} angle={50}>
                                                    <TextField label="Notas" name="notas" multiline rows={2} size="small" fullWidth sx={textFieldStyle} value={infoCliente.notas} onChange={handleChange} />
                                                </MotionFieldX>
                                            </Box>


                                            <Box sx={{ gridColumn: "1 / -1", mt: 2 }}>
                                                <motion.div className="col-md-12"
                                                    initial={{ opacity: 0, y: 50 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
                                                    whileHover={{ y: -4, scale: 1.03, filter: "brightness(1.1)", transition: { duration: 0.25, ease: "easeOut" }, }} >
                                                    <Button fullWidth variant="contained" sx={styleBtn} onClick={editarCliente}>
                                                        Editar
                                                    </Button>
                                                </motion.div>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </motion.div>
                        </>
                    </Modal>
                )}
            </AnimatePresence>

            <ToastContainer transition={Flip} position="bottom-center" />
        </>
    );
};

export default ModalEditCliente;



